import { AppId, FCX, ProductLogo } from "@datassential/platform-ui-lib";
import { PermissionLevelEnum } from "@models/PermissionSection";
import './LocalPopularity.scss'
import { useFoodProfileLocalPopularity } from "@apiHooks/useFoodProfileLocalPopularity";
import { useEffect, useMemo } from "react";
import { components } from "@apiSchema";
import BarChart from "@pages/Food/components/BarChart/BarChart";
import ExternalProductLink from "@pages/Food/components/ExternalProductLink/ExternalProductLink";
import Widget from "@components/Widget/Widget";

const Item: FCX<{
    name: string;
    value: number;
    proportionalValue: number;
    index: number;
}> = ({
    name,
    value,
    proportionalValue,
    index,
}) => {
    return (
        <div
            className="FoodLocalPopularity__item FoodSectionMenu__blur"
        >
            <div
                className="FoodLocalPopularity__item-title"
                data-testid={`textFoodMenuLocalPopularityItemName-${index}`}
            >
                {name}
            </div>
            <div className="FoodLocalPopularity__item-content">
                <BarChart
                    valuePct={proportionalValue}
                    colorStart="#006EF5"
                    colorStop="#00B4FF"
                    className="FoodLocalPopularity__item-bar"
                />
                <div
                    className="FoodLocalPopularity__item-value"
                    data-testid={`textFoodMenuLocalPopularityItemValue-${index}`}
                >
                    {value}
                </div>
            </div>
        </div>
    );
};

const FoodLocalPopularity: FCX<{
    word: string | null;
    permissionLevel: PermissionLevelEnum;
    /** Lets the legacy Menu re-arrange its grid when there's no local data. */
    setHasLocalData?: (hasLocalData: boolean) => void;
}> = ({
    word,
    permissionLevel,
    setHasLocalData,
    className,
}) => {
    const {
        data: {
            permissionLevel: permissionLevelLocalPopularity,
            data: rawData,
            link,
        },
        isLoading,
    } = useFoodProfileLocalPopularity(word);

    const resolvedData = useMemo(
        (): components['schemas']['LocalProductItemViewModel'][] => {
            if (permissionLevel !== PermissionLevelEnum.Full) {
                return [
                    { key: 'Item 1', value: 100 },
                    { key: 'Item 2', value: 90 },
                    { key: 'Item 3', value: 80 },
                    { key: 'Item 4', value: 70 },
                    { key: 'Item 5', value: 60 },
                    { key: 'Item 6', value: 50 },
                    { key: 'Item 7', value: 40 },
                    { key: 'Item 8', value: 30 },
                    { key: 'Item 9', value: 20 },
                    { key: 'Item 10', value: 10 },
                ];
            }

            return rawData;
        },
        [rawData, permissionLevel]
    );

    const data = useMemo(
        () => {
            const result = [...resolvedData];

            result.sort((a, b) => {
                if (a.value === null || a.value === undefined) return 1;
                if (b.value === null || b.value === undefined) return -1;
                return b.value - a.value;
            });

            const maxCount = result[0]?.value ?? 0;

            return result
                .slice(0, 10)
                .map(i => ({
                    name: i.key ?? '',
                    value: Math.floor(i.value ?? 0),
                    proportionalValue: ((i.value ?? 0) / maxCount) * 100,
                }));
        },
        [resolvedData]
    );

    const col1 = useMemo(
        () => data.slice(0, 5),
        [data]
    );

    const col2 = useMemo(
        () => data.slice(5, 10),
        [data]
    );

    useEffect(() => {
        setHasLocalData?.(permissionLevelLocalPopularity !== PermissionLevelEnum.None);
    }, [setHasLocalData, permissionLevelLocalPopularity]);

    if (permissionLevel !== PermissionLevelEnum.None && permissionLevelLocalPopularity === PermissionLevelEnum.None) {
        return null;
    }

    return (
        <Widget
            testId="blockFoodMenuLocalPopularity"
            headerContent={(
                <>
                    <div data-testid="textFoodMenuLocalPopularityTitle">
                        LOCAL POPULARITY
                    </div>
                    {link && (
                        <ExternalProductLink
                            appId={AppId.LocalInsights}
                            link={link}
                            testId="buttonFoodMenuLocalPopularityExternalLink"
                        />
                    )}
                </>
            )}
            className={className}
        >
            <div className="FoodLocalPopularity__wrapper">
                <div className="FoodLocalPopularity">
                    {isLoading && (
                        <div className="spinner spinner-blink is-small">
                            <ProductLogo appId={AppId.LocalInsights} type={'regular'}/>
                        </div>
                    )}
                    {!isLoading && (
                        <>
                            <div className="FoodLocalPopularity__cols">
                                <div className="FoodLocalPopularity__col">
                                    {col1.map((item, index) => (
                                        <Item
                                            key={item.name}
                                            {...item}
                                            index={index}
                                        />
                                    ))}
                                </div>
                                <div className="FoodLocalPopularity__col">
                                    {col2.map((item, index) => (
                                        <Item
                                            key={item.name}
                                            {...item}
                                            index={index + col1.length}
                                        />
                                    ))}
                                </div>
                            </div>
                            <div
                                className="Widget__footer"
                                data-testid="textFoodMenuLocalPopularityFooter"
                            >
                                indices shown; 100 = average
                            </div>
                        </>
                    )}
                </div>
            </div>
        </Widget>
    );
};

export default FoodLocalPopularity;